import { Button, Input, Modal, Select, Space, Table, message } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { serverUrl } from "../../../../../nestserver";
import { SearchOutlined } from "@ant-design/icons";

const MontessoriGenerateCard = () => {
  const { Option } = Select;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const queryParams = new URLSearchParams(location.search);
  const classIdFromUrl = queryParams.get("classId") || undefined;
  const termFromUrl = queryParams.get("term") || "Term 1";

  const [bulkNotifyTerm, setBulkNotifyTerm] = useState(termFromUrl);
  const [classId, setClassId] = useState(classIdFromUrl);
  const [progressIds, setProgressIds] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState(
    JSON.parse(localStorage.getItem("montessori-selected-rows") || "[]"),
  );
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [rowSelections, setRowSelections] = useState(
    JSON.parse(localStorage.getItem("montessori-row-selections") || "{}"),
  );

  useEffect(() => {
    localStorage.setItem(
      "montessori-selected-rows",
      JSON.stringify(selectedRowKeys),
    );
  }, [selectedRowKeys]);

  useEffect(() => {
    localStorage.setItem(
      "montessori-row-selections",
      JSON.stringify(rowSelections),
    );
  }, [rowSelections]);

  // fetch instructor's own classes
  async function fetchClasses() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/teachers/instructor-classes",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }
  const classes = useQuery("teachers/instructor-classes", fetchClasses);

  // fetch students in class with montessori card statuses
  async function fetchStudents(classId) {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/montessori-progress-card/?class=${classId}`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }

  const getStudents = useQuery(
    ["montessori-progress-card", classId],
    () => fetchStudents(classId),
    { enabled: !!classId },
  );

  // fetch templates for selected class
  async function fetchTemplates(classId) {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/montessori-progress-card-template/?classId=${classId}`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }
  const getTemplate = useQuery(
    ["montessori-progress-card-template", classId],
    () => fetchTemplates(classId),
    { enabled: !!classId },
  );

  const handleNotify = () => {
    const payload = { progress_card_ids: progressIds, term: bulkNotifyTerm };
    if (progressIds && progressIds?.length > 0) {
      axios
        .patch(serverUrl + "/montessori-progress-card/notify", payload, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
        .then((res) => {
          queryClient.refetchQueries(["montessori-progress-card", classId]);
          message.success(res?.data?.result?.message);
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            message.error(err?.response?.data?.message);
          }
        });
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={
              <SearchOutlined
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            }
            size="small"
            style={{
              width: 90,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record?.[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const columns = [
    {
      title: "Student Id",
      dataIndex: "unique_id",
      key: "unique_id",
      align: "center",
      render: (text) => <div className="text-base text-center">{text}</div>,
    },
    {
      title: "Name",
      key: "name",
      align: "center",
      ...getColumnSearchProps("first_name"),
      render: (record) => (
        <div className="text-base font-semibold text-center capitalize">
          {record?.first_name} {record?.last_name}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      render: (record) => {
        const rowData = rowSelections[record._id];
        const currentTemplateId =
          rowData?.templateId || record?.progress_card?.template?._id;

        const currentTerm =
          rowData?.term ||
          (record?.progress_card?.template?._id &&
          String(record?.progress_card?.template?._id) ===
            String(currentTemplateId)
            ? record?.progress_card?.term_metadata?.[0]?.term
            : undefined);

        const card = record?.progress_cards?.find(
          (c) =>
            String(c.template?._id || c.template) === String(currentTemplateId),
        );

        const metadata = card?.term_metadata?.find(
          (m) => m.term === currentTerm,
        );

        return (
          <div className="text-base text-center capitalize">
            {metadata?.status || "unready"}
          </div>
        );
      },
    },
    {
      title: "Last Notified",
      key: "notified_at",
      align: "center",
      render: (record) => {
        const rowData = rowSelections[record._id];
        const currentTemplateId =
          rowData?.templateId || record?.progress_card?.template?._id;

        const currentTerm =
          rowData?.term ||
          (record?.progress_card?.template?._id &&
          String(record?.progress_card?.template?._id) ===
            String(currentTemplateId)
            ? record?.progress_card?.term_metadata?.[0]?.term
            : undefined);

        const card = record?.progress_cards?.find(
          (c) =>
            String(c.template?._id || c.template) === String(currentTemplateId),
        );

        const metadata = card?.term_metadata?.find(
          (m) => m.term === currentTerm,
        );
        const text = metadata?.notified_at;

        return (
          <div className="text-base text-center capitalize">
            {text && text !== "--"
              ? moment(text).format("DD MMM, h:mm A")
              : "--"}
          </div>
        );
      },
    },
    {
      title: "Template",
      key: "template",
      align: "center",
      width: 200,
      render: (record) => {
        const currentTemplateId =
          rowSelections[record._id]?.templateId ||
          record?.progress_card?.template?._id;

        return (
          <div>
            <Select
              placeholder="Select Template"
              style={{ width: "100%" }}
              value={currentTemplateId ? String(currentTemplateId) : undefined}
              onChange={(v) => {
                setRowSelections((prev) => ({
                  ...prev,
                  [record._id]: {
                    ...prev[record._id],
                    templateId: v,
                    term: undefined,
                  },
                }));
              }}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {getTemplate?.data?.result?.map((t) => (
                <Option key={String(t._id)} value={String(t._id)}>
                  {t.name}
                </Option>
              ))}
            </Select>
            {record?.progress_cards?.length > 1 && (
              <div
                className="text-xs text-blue-500 cursor-pointer mt-1"
                onClick={() => {
                  setSelectedRecord(record.progress_cards);
                  setIsModalVisible(true);
                }}
              >
                View all ({record.progress_cards.length})
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Term",
      key: "term",
      align: "center",
      width: 150,
      render: (record) => {
        const rowData = rowSelections[record._id];
        const currentTemplateId =
          rowData?.templateId || record?.progress_card?.template?._id;

        const templateData = getTemplate?.data?.result?.find(
          (t) => String(t._id) === String(currentTemplateId),
        );
        const activeTerms = templateData?.active_terms || [];

        const currentTerm =
          rowData?.term ||
          (record?.progress_card?.template?._id &&
          String(record?.progress_card?.template?._id) ===
            String(currentTemplateId)
            ? record?.progress_card?.term_metadata?.[0]?.term
            : undefined);

        return (
          <Select
            placeholder="Select Term"
            style={{ width: "100%" }}
            value={currentTerm}
            onChange={(v) => {
              setRowSelections((prev) => ({
                ...prev,
                [record._id]: { ...prev[record._id], term: v },
              }));
            }}
            disabled={!currentTemplateId}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {activeTerms.map((t) => (
              <Option key={t} value={t}>
                {t}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (record) => {
        const rowData = rowSelections[record._id];
        const finalTemplateId =
          rowData?.templateId || record?.progress_card?.template?._id;

        const finalTerm =
          rowData?.term ||
          (record?.progress_card?.template?._id &&
          String(record?.progress_card?.template?._id) ===
            String(finalTemplateId)
            ? record?.progress_card?.term_metadata?.[0]?.term
            : undefined);

        const card = record?.progress_cards?.find(
          (c) =>
            String(c.template?._id || c.template) === String(finalTemplateId),
        );

        const metadata = card?.term_metadata?.find((m) => m.term === finalTerm);
        const isUnready = !metadata || metadata?.status === "unready";

        return (
          <div className="text-base font-semibold text-center cursor-pointer">
            <Button
              disabled={!finalTemplateId || !finalTerm}
              onClick={() => {
                const path = isUnready
                  ? `/dashboard/instructor/menu/montessori-student/${record?._id}/template/${finalTemplateId}?term=${finalTerm}`
                  : `/dashboard/instructor/menu/montessori-student/${record?._id}/template/${finalTemplateId}/report/${card?._id}?term=${finalTerm}`;
                navigate(path);
              }}
            >
              {isUnready ? "Generate" : "View"}
            </Button>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    const updatedProgressIds = getStudents?.data?.result
      ?.filter((student) => selectedRowKeys?.includes(student?._id))
      ?.map((student) => {
        const rowData = rowSelections[student._id];
        const finalTemplateId =
          rowData?.templateId || student?.progress_card?.template?._id;

        const card = student?.progress_cards?.find(
          (c) =>
            String(c.template?._id || c.template) === String(finalTemplateId),
        );

        const metadata = card?.term_metadata?.find(
          (m) => m.term === bulkNotifyTerm,
        );

        // Only include if card exists and status is ready or notified for THIS term
        if (
          card?._id &&
          (metadata?.status === "ready" || metadata?.status === "notified")
        ) {
          return card._id;
        }
        return null;
      })
      ?.filter((id) => id !== null && id !== undefined);

    setProgressIds(updatedProgressIds || []);
  }, [
    getStudents?.data,
    selectedRowKeys,
    rowSelections,
    bulkNotifyTerm,
    getStudents?.isSuccess,
  ]);

  const onSelectChange = (selectedRowKeys) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <>
      <div className="mt-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className="bg-white drop-shadow-md p-8 w-full"
        >
          <div className="text-2xl text-orange-500 font-semibold pb-8">
            Generate Report Card
          </div>
          <div className="flex gap-8 items-center pb-4">
            <div className="flex gap-4 items-center">
              <h3 className="text-base font-semibold">Class</h3>
              <Select
                showSearch
                style={{ width: 200 }}
                placeholder="Select Class"
                value={classId}
                onChange={(v) => {
                  setClassId(v);
                  setRowSelections({});
                  setSelectedRowKeys([]);
                  queryParams.set("classId", v);
                  navigate({ search: queryParams.toString() });
                }}
              >
                {classes?.data?.map((data) => (
                  <Option key={data._id} value={data._id}>
                    {data.name}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
          <div className="py-5">
            <Table
              rowSelection={{ type: "checkbox", ...rowSelection }}
              rowKey="_id"
              columns={columns}
              dataSource={getStudents?.data?.result || []}
              pagination={true}
              loading={getStudents?.isLoading}
            />
            {classId && (
              <div className="flex justify-center items-center py-5 gap-4">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Term : &nbsp;</span>
                  <Select
                    style={{ width: 120, textAlign: "center" }}
                    value={bulkNotifyTerm}
                    onChange={(v) => {
                      setBulkNotifyTerm(v);
                      queryParams.set("term", v);
                      navigate({ search: queryParams.toString() });
                    }}
                  >
                    <Option value="Term 1">Term 1</Option>
                    <Option value="Term 2">Term 2</Option>
                    <Option value="Term 3">Term 3</Option>
                  </Select>
                </div>
                <Button
                  size="large"
                  type="primary"
                  onClick={handleNotify}
                  disabled={progressIds?.length === 0}
                >
                  Notify
                </Button>
              </div>
            )}
          </div>
          <Modal
            title="Applied Templates"
            visible={isModalVisible}
            onOk={() => setIsModalVisible(false)}
            onCancel={() => setIsModalVisible(false)}
          >
            <ul>
              {selectedRecord
                ?.slice()
                .reverse()
                .map((template, index) => (
                  <li
                    key={template?.template?._id || template?.template || index}
                    className="capitalize"
                  >
                    {`${index + 1}. ${
                      template?.template?.name ||
                      template?.name ||
                      template?.template ||
                      "Template"
                    }`}
                  </li>
                ))}
            </ul>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default MontessoriGenerateCard;
